import {
  ApiBlueprint,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import type { FilterPredicate } from '@backstage/filter-predicates';
import { SANDBOXED_REPO_SLUG_ANNOTATION } from '@internal/plugin-sandboxed-common';
import { SandboxedClient, sandboxedApiRef } from './api';

/**
 * Any entity naming a GitHub repo, except the third-party ones. `catalog/npm`
 * indexes packages this org does not own — hono, daisyUI — and they carry a
 * project-slug too, so the namespace clause keeps the tab off them.
 *
 * Cosmetic only: the filter is a predicate over the entity and anyone can call
 * the API with any ref, so the backend's owned-org check is the enforcement.
 */
const hasRepo: FilterPredicate = {
  $all: [
    {
      [`metadata.annotations.${SANDBOXED_REPO_SLUG_ANNOTATION}`]: {
        $exists: true,
      },
    },
    { $not: { 'metadata.namespace': 'external' } },
  ],
};

const sandboxedApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams({
      api: sandboxedApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new SandboxedClient(discoveryApi, fetchApi),
    }),
});

const sandboxedEntityContent = EntityContentBlueprint.make({
  params: {
    path: 'sandboxed',
    title: 'Sandboxes',
    filter: hasRepo,
    loader: async () => {
      const { EntitySandboxedContent } = await import(
        './components/EntitySandboxedContent'
      );
      return <EntitySandboxedContent />;
    },
  },
});

// No overview card: a card would have to answer "is anything running" for a
// component that usually has nothing running, and the tab is one click away.
export const sandboxedPlugin = createFrontendPlugin({
  pluginId: 'sandboxed',
  extensions: [sandboxedApi, sandboxedEntityContent],
});
